import { FormattedMessage } from 'react-intl';
import { EditButton } from '@/components/EditButton';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { LocalDepartment } from '@/routes/$tenantId/_layout/departments/_layout/$departmentId/$view';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loading } from '../Loading';
import { useFlattenDepartmentTree } from '@/hooks/useFlattenDepartmentTree';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import {
  DepartmentDTO,
  getGetCurrentDepartmentTreeQueryKey,
  getGetDepartmentByIDQueryKey,
  getGetDepartmentsListQueryKey,
  useUpdateDepartmentByID,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { updateDepartmentByIDBody } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { trimData } from '@/helpers/trimData';
import { FormSubmitActions } from '../FormSubmitActions';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { formatDate } from '@/helpers/formatting';

const departmentFormSchema = updateDepartmentByIDBody;

type DepartmentType = z.infer<typeof departmentFormSchema>;

interface DepartmentDetailsProps {
  department: LocalDepartment;
}

export const DepartmentDetailsForm: FC<DepartmentDetailsProps> = (props) => {
  const { department } = props;
  const [isEditMode, setIsEditMode] = useState(false);

  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const customErrorMap = useCustomErrorMap;
  const queryClient = useQueryClient();

  const { flattenedDepartments, flattenTreeLoading } =
    useFlattenDepartmentTree();

  const updateDepartment = useUpdateDepartmentByID();

  const methods = useForm<DepartmentType>({
    defaultValues: department,
    resolver: zodResolver(departmentFormSchema, {
      errorMap: customErrorMap(),
    }),
  });

  const { handleSubmit, control, formState, reset } = methods;

  const parentDepartment = useMemo(() => {
    if (!flattenedDepartments) return;

    return flattenedDepartments.find(
      (dept) => dept.id === department?.parent_id,
    );
  }, [flattenedDepartments, department]);

  const startEdit = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const cancelEdit = useCallback(
    (updatedDepartment: DepartmentDTO) => {
      reset(updatedDepartment);
      setIsEditMode(false);
    },
    [reset],
  );

  const onSubmit: SubmitHandler<DepartmentType> = (data) => {
    if (!department.id) {
      console.error('Department ID is missing');
      return;
    }
    const submitData = JSON.parse(JSON.stringify(trimData(data)));

    updateDepartment.mutate(
      { departmentID: department.id, data: submitData },
      {
        onSuccess: async () => {
          toast.success(
            { title: `${data.name} uppdaterades` },
            { autoClose: 3000 },
          );

          await queryClient.invalidateQueries({
            queryKey: getGetCurrentDepartmentTreeQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: getGetDepartmentsListQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: getGetDepartmentByIDQueryKey(department?.id ?? ''),
          });

          cancelEdit(submitData);
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök spara igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  };

  if (flattenTreeLoading) return <Loading />;

  if (!department) {
    return (
      <FormattedMessage defaultMessage="Ingen organisationsinformation hittad" />
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col border-b border-b-background-200 py-4 @container md:py-8"
        ref={ref}
      >
        <div className="mb-4 flex w-full flex-row justify-between">
          <h2 className="w-full">
            <FormattedMessage defaultMessage="Information" />
          </h2>
          {!isEditMode && hasDepartmentWriteAccess(department.id) && (
            <EditButton onClickButton={startEdit} />
          )}
        </div>
        <div className="mb-8 grid grid-cols-1 flex-wrap gap-4 @xl:grid-cols-3 @2xl:grid-cols-4">
          {parentDepartment ? (
            <>
              <div className="flex flex-col">
                <span className="font-medium text-text-900">
                  <FormattedMessage defaultMessage="Överordnad avdelning" />
                </span>
                <span>{parentDepartment?.name}</span>
              </div>
              <DepartmentFormElement
                label={<FormattedMessage defaultMessage="Avdelningsnamn" />}
                editable={true}
                input={
                  <InputText
                    name="name"
                    control={control}
                    editMode={isEditMode}
                    error={formState.errors.name}
                  />
                }
              />
            </>
          ) : (
            <>
              <DepartmentFormElement
                label={
                  <FormattedMessage defaultMessage="Organisationsnummer" />
                }
                editable={true}
                input={<p>{department.organization_number}</p>}
              />
              <DepartmentFormElement
                label={<FormattedMessage defaultMessage="Organisationsnamn" />}
                editable={true}
                input={<p>{department.name}</p>}
              />
            </>
          )}
        </div>
        {!parentDepartment && Boolean(department.delivery_address?.street) && (
          <>
            <h4 className="mb-4 w-full">
              <FormattedMessage defaultMessage="Leveransadress" />
            </h4>
            <div className="mb-8 grid grid-cols-1 flex-wrap gap-4 @xl:grid-cols-3 @2xl:grid-cols-4">
              <DepartmentFormElement
                label={<FormattedMessage defaultMessage="Adress" />}
                editable={Boolean(department.delivery_address?.street)}
                input={
                  <InputText
                    name="delivery_address.street"
                    control={control}
                    editMode={isEditMode}
                    error={formState.errors.delivery_address?.street}
                  />
                }
              />
              <DepartmentFormElement
                label={<FormattedMessage defaultMessage="Postnummer" />}
                editable={Boolean(department.delivery_address?.zip_code)}
                input={
                  <InputPostalCode
                    name="delivery_address.zip_code"
                    control={control}
                    editMode={isEditMode}
                    error={formState.errors.delivery_address?.zip_code}
                  />
                }
              />
              <DepartmentFormElement
                label={<FormattedMessage defaultMessage="Postort" />}
                editable={Boolean(department.delivery_address?.city)}
                input={
                  <InputText
                    name="delivery_address.city"
                    control={control}
                    editMode={isEditMode}
                    error={formState.errors.delivery_address?.city}
                  />
                }
              />
            </div>
            <p className="flex flex-wrap gap-4 text-text-700">
              <span>
                <FormattedMessage
                  defaultMessage="Skapad: {date}"
                  values={{ date: formatDate(department.created_at ?? '') }}
                />
              </span>
              <span>
                <FormattedMessage
                  defaultMessage="Uppdaterad: {date}"
                  values={{ date: formatDate(department.updated_at ?? '') }}
                />
              </span>
            </p>
          </>
        )}
        <FormSubmitActions
          active={isEditMode}
          createMode={false}
          onCancel={() => cancelEdit(department as unknown as DepartmentDTO)}
          onSubmit={handleSubmit(onSubmit)}
          isLoading={updateDepartment.isPending}
        />
      </form>
    </FormProvider>
  );
};

interface DetailItemProps {
  label: ReactNode;
  input?: ReactNode;
  editable: boolean;
}

const DepartmentFormElement = ({ label, input, editable }: DetailItemProps) => {
  if (!editable) return null;

  return (
    <div className="flex w-full min-w-[150px] flex-col sm:w-auto sm:flex-1">
      <span className="font-medium text-text-900">{label}</span>
      {input}
    </div>
  );
};
